import { FC, useCallback, useEffect, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from '@mui/icons-material';
import { Box, Button, Chip, InputAdornment } from '@mui/material';
import classnames from 'classnames';

import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import FormRichEditor from '@components/FormRichEditor/FormRichEditor';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import ViewProductPageButton from '@components/ViewProductPageButton/ViewProductPageButton';
import useToggle from '@hooks/useToggle';
import { IAdminSupplementInquiryResponseFormValues } from '@interfaces/adminSupplements';
import { handleAdminCreateNewSupplement } from '@store/features/adminInquiries/newSupplement/actions';
import { adminNewSupplementPendingNewSupplementSelector } from '@store/features/adminInquiries/newSupplement/selectors';
import { setAdminPendingNewSupplement } from '@store/features/adminInquiries/newSupplement/slice';
import {
  handleAdminDeactivateSupplement,
  handleAdminSupplementRenew,
} from '@store/features/adminInquiries/supplementDetails/actions';
import {
  adminSupplementDetailsIsDeactivatingSelector,
  adminSupplementDetailsSelector,
} from '@store/features/adminInquiries/supplementDetails/selectors';
import useAdminDrugClassesByProgramCode from '@store/hooks/useAdminDrugClassesByProgramCode';
import useAdminRiskLevelsByProgramCode from '@store/hooks/useAdminRiskLevelsByProgramCode';
import useDispatch from '@store/hooks/useDispatch';
import useSupplementCategories from '@store/hooks/useSupplementCategories';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import CannedResponses from '../../CannedResponses/CannedResponses';
import IngredientsSearchSelectBox from '../IngredientsSearchSelectBox/IngredientsSearchSelectBox';
import { appendComment } from '../utils';

import { ISupplementInformationProps } from './SupplementInformationForm.interface';
import {
  convertFormValuesToCreateNewPayload,
  convertFormValuesToRenewPayload,
  encodeHtmlText,
  getInitialValues,
  getParsedText,
  validateForm,
} from './utils';

import './style.scss';

const SupplementInformationForm: FC<ISupplementInformationProps> = ({ className }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const eventBus = useEventBus('supplementInformationForm');

  const adminSupplementInquiryDetails = useSelector(adminSupplementDetailsSelector);
  const adminDrugClasses = useAdminDrugClassesByProgramCode(
    adminSupplementInquiryDetails?.programCodeId,
  );
  const adminRiskLevels = useAdminRiskLevelsByProgramCode(
    adminSupplementInquiryDetails?.programCodeId,
  );
  const supplementCategories = useSupplementCategories();
  const isDeactivating = useSelector(adminSupplementDetailsIsDeactivatingSelector);
  const pendingNewSupplement = useSelector(adminNewSupplementPendingNewSupplementSelector);

  const [
    isDeactivateConfirmationOpen,
    handleOpenDeactivateConfirmation,
    handleCloseDeactivateConfirmation,
  ] = useToggle();

  const initialValues = useMemo(() => {
    const values = getInitialValues(adminSupplementInquiryDetails, pendingNewSupplement);
    return {
      ...values,
      comments: getParsedText(values.comments),
    };
  }, [adminSupplementInquiryDetails, pendingNewSupplement]);

  const getAdminDrugClassName = useCallback(
    (drugClassId: number) => adminDrugClasses.find(drugClass => drugClass.id === drugClassId)?.name,
    [adminDrugClasses],
  );

  const handleCreateNew = useCallback(
    (values: IAdminSupplementInquiryResponseFormValues) => {
      const data = convertFormValuesToCreateNewPayload(values);

      return dispatch(
        handleAdminCreateNewSupplement({
          ...data,
          comments: encodeHtmlText(data.comments),
        }),
      );
    },
    [dispatch],
  );

  const handleRenew = useCallback(
    (values: IAdminSupplementInquiryResponseFormValues) => {
      const data = convertFormValuesToRenewPayload(values);
      return dispatch(handleAdminSupplementRenew(data));
    },
    [dispatch],
  );

  const handleDeactivate = useCallback(
    () =>
      dispatch(
        handleAdminDeactivateSupplement(adminSupplementInquiryDetails?.supplementInquiryId!),
      ),
    [dispatch, adminSupplementInquiryDetails],
  );

  const handleCancel = useCallback(() => {
    dispatch(setAdminPendingNewSupplement(null));
  }, [dispatch]);

  useEffect(() => handleCancel, [handleCancel]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_CREATE_NEW_SUPPLEMENT_SUCCESS, () =>
      handleToast('success', t('adminNewSupplement.submittedSuccessfully')),
    );

    eventBus.on(EventType.ADMIN_RENEW_SUPPLEMENT_SUBMITTED, () =>
      handleToast('success', t('adminInquiries.renewSupplementResponseSuccess')),
    );

    eventBus.on(EventType.ADMIN_DEACTIVATE_SUPPLEMENT_SUCCESS, () => {
      handleToast('success', t('adminInquiries.deactivateSupplementSuccess'));
      handleCloseDeactivateConfirmation();
    });

    return () =>
      eventBus.off([
        EventType.ADMIN_CREATE_NEW_SUPPLEMENT_SUCCESS,
        EventType.ADMIN_RENEW_SUPPLEMENT_SUBMITTED,
        EventType.ADMIN_DEACTIVATE_SUPPLEMENT_SUCCESS,
      ]);
  }, [t, eventBus, handleCloseDeactivateConfirmation]);

  return (
    <>
      <Box className={classnames('admin-supplement-information', className)}>
        <Form
          onSubmit={!pendingNewSupplement ? handleRenew : handleCreateNew}
          initialValues={initialValues}
          validate={validateForm}
          render={({ form, handleSubmit, submitting, values }) => (
            <form className="admin-supplement-information__form" noValidate>
              <p className="subtitle-big text-bold admin-supplement-information__title">
                {t('inquiryResponse.needsInfoTitle')}
              </p>

              <Field
                component={FormTextField}
                className="admin-supplement-information__input"
                label={t('addSupplementModal.supplementName')}
                name="name"
                format={removeWhiteSpaces}
                formatOnBlur
                multiline
                required
              />

              <Field
                component={FormTextField}
                className="admin-supplement-information__input"
                label={t('addSupplementModal.companyName')}
                name="companyName"
                disabled={!pendingNewSupplement}
                format={removeWhiteSpaces}
                formatOnBlur
              />

              <Box className="admin-supplement-information__column">
                <Field
                  component={FormSelect}
                  className="admin-supplement-information__input"
                  label={t('riskLevels.title')}
                  options={adminRiskLevels.map(riskLevel => ({
                    id: riskLevel.riskLevelId,
                    name: riskLevel.name,
                  }))}
                  name="riskLevelId"
                />

                <Field
                  component={FormSelect}
                  className="admin-supplement-information__input"
                  label={t('addSupplementModal.productCategory')}
                  name="supplementCategoryId"
                  options={supplementCategories}
                  disabled={!pendingNewSupplement}
                />
              </Box>

              <Box className="admin-supplement-information__column">
                <Field
                  component={FormTextField}
                  className="admin-supplement-information__input admin-supplement-information__column_75 admin-supplement-information__link"
                  label={t('addSupplementModal.linkToProductPage')}
                  name="productLink"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment className="new-supplement-form__input-icon" position="start">
                        <Link color="inherit" />
                      </InputAdornment>
                    ),
                  }}
                  format={removeWhiteSpaces}
                  formatOnBlur
                  required
                />

                <ViewProductPageButton
                  linkToProductPage={values.productLink}
                  className="admin-supplement-information__column_25"
                />
              </Box>

              <Field
                component={FormSelect}
                className="admin-supplement-information__input"
                label={t('inquiryResponse.drugClass')}
                options={adminDrugClasses}
                name="drugClassIds"
                multiple
                renderValue={(selected: number[]) => (
                  <Box className="admin-supplement-information__select-chip">
                    {selected.map(id => (
                      <Chip
                        key={id}
                        label={getAdminDrugClassName(id)}
                        title={getAdminDrugClassName(id)}
                      />
                    ))}
                  </Box>
                )}
              />

              <Field
                component={IngredientsSearchSelectBox}
                className="admin-supplement-information__input"
                label={t('inquiryResponse.ingredients')}
                name="ingredients"
              />

              <p className="subtitle-big text-bold admin-supplement-information__title">
                {t('inquiryResponse.comments')}

                <CannedResponses
                  onApply={text => {
                    form.change('comments', appendComment(getParsedText(text), values.comments));
                  }}
                />
              </p>

              <Field
                component={FormRichEditor}
                className="admin-supplement-information__input"
                label={t('inquiryResponse.comments')}
                name="comments"
              />

              <Box className="admin-supplement-information__column">
                <Button
                  type="submit"
                  variant="contained"
                  className="admin-supplement-information__button"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {!pendingNewSupplement
                    ? t('adminInquiries.renew')
                    : t('adminNewSupplement.create')}
                </Button>

                <Button
                  variant="outlined"
                  className="admin-supplement-information__button"
                  onClick={!pendingNewSupplement ? handleOpenDeactivateConfirmation : handleCancel}
                >
                  {!pendingNewSupplement
                    ? t('adminInquiries.deactivateSupplement')
                    : t('common.cancel')}
                </Button>
              </Box>
            </form>
          )}
        />
      </Box>

      <ConfirmationModal
        open={isDeactivateConfirmationOpen}
        isLoading={isDeactivating}
        onConfirm={handleDeactivate}
        onClose={handleCloseDeactivateConfirmation}
        title={t('adminInquiries.deactivateSupplementConfirmation')}
        rightActionTitle={t('adminInquiries.deactivateSupplement')}
      />
    </>
  );
};

export default SupplementInformationForm;
